import React, {
  useContext, useEffect, useMemo, useState,
} from 'react';
import GiftCard from './GiftCard/GiftCard';
import CheckoutPageContext from '../../../context/CheckoutPageContext';
import PaymentHeader from './PaymentHeader';
import PaymentOptionsContainer from './PaymentOptionsContainer';
import { isPageUrlContains } from '../../../tools/pageHelper';
import CartPaymentDetails from './CartPaymentDetails';
import { store } from '../../useLog/Logger/Utils/Store/Store';
import useLog from '../../useLog/useLog';
import useSwitchesFromContext from '../../../hooks/useSwitchesFromContext';
import BillingAddressContainer from '../BillingAddress/BillingAddressContainer';
import { dispatchPaymentTypeChangedEvent } from '../../../tools/paymentTypeChangedEvent';
import { useStepsContext } from '../../Steps/StepsContext';
import { PAYMENT_OPTIONS_WITHOUT_BILLING } from '../../../tools/constants';

export default function PaymentManager() {
  const logger = useLog('checkout.PaymentManager');
  const { checkoutPageState, updateCheckoutPageState } = useContext(CheckoutPageContext);
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const { getSwitchValue } = useSwitchesFromContext();
  const {
    bagItems,
    cartPayment,
    orderTotals,
    paymentOptions,
    selectedPaymentType,
    giftCards,
  } = checkoutPageState;
  const isKlarnaOrApmSelected = (
    selectedPaymentType === 'klarna'
    || selectedPaymentType === 'apm'
  );
  const hasPaymentsMfe = getSwitchValue('hasPaymentSectionMFE');
  const isBillingAddressEnabled = getSwitchValue('chkMfeBillingAddressEnabled');
  const hasPaypalError = !!store.localStorage.get('hasPaypalError');
  const isPayPalExpressCheckout = isPageUrlContains('/ProcessPayPalReturnAndOrderExpress');
  const orderId = orderTotals?.orderId;
  const grandTotal = orderTotals?.grandTotal;
  let currentStep = null;
  try {
    const stepsContext = useStepsContext();
    currentStep = stepsContext.currentStep;
  } catch (error) {
    // Steps context not available
    currentStep = null;
  }

  const isBillingAddressMFEVisible = useMemo(
    () => {
      if (!isBillingAddressEnabled) return false;

      if (selectedPaymentType) {
        return !PAYMENT_OPTIONS_WITHOUT_BILLING.includes(selectedPaymentType);
      }

      if (bagItems?.hasOnlyEGiftCard) return true;

      return false;
    },
    [isBillingAddressEnabled, selectedPaymentType, bagItems],
  );

  const isGiftCardApplied = useMemo(() => {
    if (!isBillingAddressEnabled) return false;
    return grandTotal <= 0 && giftCards?.appliedGiftCards?.length > 0;
  }, [isBillingAddressEnabled, grandTotal, giftCards]);

  const isBillingDisabledForPayment = useMemo(() => {
    if (!isBillingAddressEnabled) return false;
    return PAYMENT_OPTIONS_WITHOUT_BILLING.includes(selectedPaymentType);
  }, [isBillingAddressEnabled, selectedPaymentType]);

  useEffect(() => {
    // Cleanup function
    if (grandTotal === 0) {
      updateCheckoutPageState({ selectedPaymentType: '' });
      dispatchPaymentTypeChangedEvent(null);
    }
  }, [updateCheckoutPageState, grandTotal]);

  if (!mounted) { return null; }

  const renderPaymentOptionsInMfe = () => {
    if (
      hasPaymentsMfe
      && grandTotal > 0
      && (
        !isPayPalExpressCheckout
        || (isPayPalExpressCheckout && hasPaypalError)
      )
      && !paymentOptions?.length
    ) {
      logger.error(`ERR: payment options not available: ${JSON.stringify({
        paymentOptions,
        hasPaymentsMfe,
        orderId,
        isPayPalExpressCheckout,
        hasPaypalError,
        cartPayment,
      })}`);
    }
    return (
      hasPaymentsMfe
      && grandTotal > 0
      && (
        !isPayPalExpressCheckout
        || (isPayPalExpressCheckout && hasPaypalError)
      )
      && paymentOptions?.length > 0
      && <PaymentOptionsContainer />
    );
  };

  const renderCartPaymentDetailsInMfe = () => {
    if (
      hasPaymentsMfe
      && isPayPalExpressCheckout
      && grandTotal > 0
      && !cartPayment
      && !hasPaypalError
    ) {
      logger.error(`ERR: cart payment not available: ${JSON.stringify({
        hasPaymentsMfe,
        orderId,
        isPayPalExpressCheckout,
        hasPaypalError,
        cartPayment,
      })}`);
    }
    return (
      hasPaymentsMfe
      && isPayPalExpressCheckout
      && cartPayment
      && !hasPaypalError
      && <CartPaymentDetails cartPayment={cartPayment} />
    );
  };

  return (
    <>
      <div className="payment-manager">
        {
          !isPayPalExpressCheckout
          && (
            <>
              {!currentStep && <PaymentHeader />}
              {checkoutPageState?.config?.numberOfGiftCardsAllowed > 0
                && !isKlarnaOrApmSelected
                && (
                  <GiftCard />
                )}
            </>
          )
        }
        {renderPaymentOptionsInMfe()}
        {renderCartPaymentDetailsInMfe()}
      </div>
      {isBillingAddressMFEVisible && <BillingAddressContainer />}
      {(isGiftCardApplied || isBillingDisabledForPayment) && <input type="hidden" name="billingAddressSameAsShipping" data-testid="billingAddressSameAsShipping" value />}
    </>
  );
}

PaymentManager.validationFields = (checkoutPageState, formValues = {}) => {
  let requiredFields = {};
  const { billingAddressSameAsShipping = false } = formValues;
  const {
    selectedPaymentType = '',
    selectedSavedPayment = null,
    cartPayment = null,
    billingAddressConfig = null,
  } = checkoutPageState || {};

  if (billingAddressConfig
    && !cartPayment
    && !billingAddressSameAsShipping
    && !!selectedPaymentType
    && !PAYMENT_OPTIONS_WITHOUT_BILLING.includes(selectedPaymentType)
  ) {
    requiredFields = billingAddressConfig?.addressFields?.reduce((acc, item) => {
      if (item?.visibility && item?.required) {
        acc[item.name] = '';
      }
      return acc;
    }, {}) || {};
  }

  if (selectedPaymentType === 'credit card') {
    if (selectedSavedPayment) {
      requiredFields['creditCard[0].cvv'] = '';
    } else {
      requiredFields['creditCard[0].cardNumber'] = '';
      requiredFields['creditCard[0].cardExpiryFmt'] = '';
      requiredFields['creditCard[0].cvv'] = '';
    }
  }

  return requiredFields;
};
